/**
 * The wave director (TRD-06 §3).
 *
 * The schedule is built in full at wave start and then consumed (R-06-08). Computing
 * spawns lazily each tick would make the spawn order depend on how the groups happen to
 * be walked, which is the sort of thing that stays correct until someone reorders a loop
 * for readability and silently invalidates every stored replay.
 *
 * There are no enemy ids in this file, and there must never be any (TRD-06 AC). Wave
 * composition is authored data; a hardcoded id here would mean daily runs generated by
 * TRD-13 could not express something the authored waves could.
 */
import {
  ENEMY_HP_GROWTH,
  ENEMY_HP_LATE_FROM_WAVE,
  ENEMY_HP_LATE_GROWTH,
  TICK_RATE,
} from '../constants.js';
import type { EnemyTable, WaveSpec } from '../scenario.js';
import type { EnemyId } from '../types.js';

/**
 * `pathIndex` sentinel: pick a lane at spawn time instead of at schedule time.
 *
 * Rolled in the World rather than here because the roll must come off the seeded gameplay
 * stream, and this function is deliberately pure — it is handed its jitter rather than
 * drawing it.
 */
export const ROLL_LANE = -1;

export interface ScheduledSpawn {
  tick: number;
  enemy: EnemyId;
  /**
   * The lane this spawn walks, or `ROLL_LANE` for "decide when it spawns".
   *
   * A whole group used to share one lane, which is why a two-lane map read as two solid
   * columns of enemies and why a board could be built for lane 1 and left alone. An
   * authored `group.pathId` still wins — a wave that deliberately sends something down a
   * particular lane keeps doing so.
   */
  pathIndex: number;
  hpMultiplier: number;
}

/**
 * The global wave health curve (R-06-10).
 *
 * Repeated multiplication rather than `Math.pow`: the exponent is small (28 waves at
 * most) and the determinism contract forbids the operator outright, since its last bits
 * differ between engines and this number scales every enemy in the wave.
 */
export function waveHpScale(waveNumber: number): number {
  let scale = 1;
  for (let w = 2; w <= waveNumber; w++) {
    scale *= ENEMY_HP_GROWTH;
    if (w >= ENEMY_HP_LATE_FROM_WAVE) scale *= ENEMY_HP_LATE_GROWTH;
  }
  return scale;
}

/**
 * Flatten a wave into a sorted spawn list.
 *
 * `hpMultiplier` folds together three separate things so the spawner stays a
 * one-multiplication step: the group's authored multiplier, the global wave curve
 * (R-06-10), and this wave's single HP jitter draw (R-06-09).
 *
 * The jitter is ONE draw for the whole wave, taken by the caller and passed in. Drawing
 * per enemy would burn RNG state proportional to enemy count — which makes divergence
 * hunting miserable, because the stream position after wave 12 then depends on exactly
 * how many splitters split — and it makes a seeded run feel merely random.
 */
export function buildSpawnSchedule(
  wave: WaveSpec,
  waveNumber: number,
  hpJitter: number,
  pathCount: number,
): ScheduledSpawn[] {
  const waveScale = waveHpScale(waveNumber);
  const spawns: ScheduledSpawn[] = [];

  for (let groupIndex = 0; groupIndex < wave.groups.length; groupIndex++) {
    const group = wave.groups[groupIndex];
    if (!group) continue;

    const pathIndex =
      group.pathId !== undefined && pathCount > 0
        ? group.pathId % pathCount
        : pathCount > 1
          ? ROLL_LANE
          : 0;

    for (let i = 0; i < group.count; i++) {
      spawns.push({
        tick: Math.round((group.delay + i * group.spacing) * TICK_RATE),
        enemy: group.enemy,
        pathIndex,
        hpMultiplier: group.hpMultiplier * waveScale * hpJitter,
      });
    }
  }

  // Stable by construction: groups are walked in declaration order and members in index
  // order, so the only thing left to order is `tick`. A stable sort therefore reproduces
  // (tick, groupIndex, indexWithinGroup) exactly as R-06-08 requires. Array.prototype.sort
  // is specified as stable, so this needs no tie-break of its own.
  spawns.sort((a, b) => a.tick - b.tick);
  return spawns;
}

/** R-06-09 — ±8% around 1, from a single draw in [0, 1). */
export function hpJitterFrom(roll: number): number {
  return 1 + (roll - 0.5) * 0.16;
}

export function archetypeFor(table: EnemyTable, id: EnemyId): EnemyTable[string] | undefined {
  return table[id];
}
